import React, { useEffect } from "react";
import {
  Button,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { editClause } from "../../actions/clauses-actions";
import * as yup from "yup";
import { styles } from "../styles";
import { REQUIRED_MESSAGE } from "../../constants";
import MentionInput from "./mentions/mention";
import { getMentions } from "../../actions/clauses/clauses.action";
import Regex from "../../utils/validators/regex";

const EditClausesContracts = ({
  data,
  closeOpenEdit,
  message,
  setMessage,
  reset,
}) => {
  const classes = styles();

  const [submitDisabled, setSubmitDisabled] = React.useState(false);
  const [definitionClause, setDefinitionClause] = React.useState(
    data?.definitionClause
  );
  const [mentionsData, setMentionsData] = React.useState([]);
  const [isDynamic, setIsDynamic] = React.useState(data?.isDynamic);

  const schema = yup.object().shape({
    name: yup
      .string()
      .nullable()
      .required(REQUIRED_MESSAGE)
      .typeError("Ingrese un nombre válido")
      .min(5, "Ingrese mínimo 5 caracteres")
      .max(200, "Ingrese máximo 200 caracteres")
      .matches(/^([^0-9]*)$/, "Ingrese un nombre válido"),
    definitionClause: yup.string(),
  });

  const { register, handleSubmit, errors, setValue, setError, clearErrors } =
    useForm({
      resolver: yupResolver(schema),
      mode: "onTouched",
      reValidateMode: "onChange",
    });

  const setDataForm = () => {
    setValue("name", data.name);
    setDefinitionClause(data.definitionClause);
    setIsDynamic(data.isDynamic);
  };

  React.useEffect(() => {
    getMentions().then((response) => {
      if (response && response.status === 200) {
        setMentionsData(response.data);
      }
    });
  }, []);

  useEffect(() => {
    if (data) {
      setDataForm();
    }
  }, [data]);

  const handleClickClose = () => {
    setMessage({ ...message, open: false });
    setSubmitDisabled(false);
    closeModal();
  };

  const handleClickCloseSuccess = () => {
    setMessage({ ...message, open: false });
    closeModal();
    reset();
  };

  const handleClick = async (dataClause) => {
    setMessage({ ...message, open: false });
    const newData = { ...dataClause, id: data.id };
    const response = await editClause(newData);
    if (response && (response.status === 200 || response.status === 201)) {
      setMessage({
        description: `La información ha sido modificada`,
        textButtonSubmit: "Aceptar",
        handleClick: () => handleClickCloseSuccess(),
        oneButtons: true,
        type: "SUCCESS",
        open: true,
      });

      return;
    }

    if (
      response?.status > 399 &&
      response?.status !== 401 &&
      response?.status !== 403
    ) {
      setMessage({
        description: response?.data.detail,
        textButtonSubmit: "Aceptar",
        handleClick: () => handleClickCloseSuccess(),
        oneButtons: true,
        type: "WARNING",
        open: true,
      });

      return;
    }

    setMessage({
      description: `Ocurrió un error procesando la solicitud, inténtelo nuevamente`,
      textButtonSubmit: "Aceptar",
      handleClick: () => handleClickClose(),
      oneButtons: true,
      type: "WARNING",
      open: true,
    });
  };

  const handleChangeMentions = (value) => {
    if (!value || value.length <= 0) {
      setError("definitionClause", {
        type: "required",
        message: REQUIRED_MESSAGE,
      });

      return;
    }

    if (value.length > 0 && value.length < 30) {
      setError("definitionClause", {
        type: "min",
        message: "Ingrese mínimo 30 caracteres",
      });

      return;
    }

    if (value.length > 30 && value.length > 5000) {
      setError("definitionClause", {
        type: "max",
        message: "Ingrese máximo 5000 caracteres",
      });

      return;
    }

    clearErrors("definitionClause");
    setDefinitionClause(value);
  };

  const onSubmit = handleSubmit((dataClause) => {
    const { name } = dataClause;

    setSubmitDisabled(true);

    setMessage({
      description: `¿Está seguro que desea modificar la cláusula?`,
      textButtonSubmit: "Aceptar",
      handleClick: () => handleClick({ name, definitionClause, isDynamic }),
      handleClickOut: () => handleClickClose(),
      type: "WARNING",
      open: true,
    });
  });

  const handleSwitchChange = (event) => {
    const { checked } = event.target;
    setIsDynamic(checked);
  };

  const closeModal = () => {
    closeOpenEdit(false);
  };

  const handleClearMentions = () => {
    setDefinitionClause((state) =>
      state.replace(Regex.DYNAMIC_KEY_PATTERN_GLOBAL, "")
    );

    setMessage({ ...message, open: false });
  };

  const closeMentionsMessage = () => {
    setIsDynamic(true);
    setDefinitionClause(data.definitionClause);
    setMessage({ ...message, open: false });
  };

  React.useEffect(() => {
    if (!isDynamic && Regex.DYNAMIC_KEY_PATTERN.test(definitionClause)) {
      setMessage({
        description: `Si desactiva la opción de cláusula dinámica, se eliminarán las menciones asociadas a esta cláusula. ¿Está seguro que desea continuar?`,
        textButtonSubmit: "Aceptar",
        handleClick: () => handleClearMentions(),
        handleClickOut: () => closeMentionsMessage(),
        type: "QUESTION",
        open: true,
      });
    }
  }, [isDynamic]);

  return (
    <div>
      <form onSubmit={onSubmit} className={classes.form} autocomplete="off">
        <Grid container spacing={2} direction="row">
          <Grid item xs={12} md={6} lg={5} style={{ width: "inherit" }}>
            <FormControl fullWidth size="small" shrink={true} notched={true}>
              <TextField
                fullWidth
                native
                size="small"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                className={classes.dropdown}
                inputRef={register}
                type="text"
                label="Nombre de cláusula *"
                name="name"
                error={errors.hasOwnProperty("name") && errors["name"].message}
                helperText={
                  errors.hasOwnProperty("name") && errors["name"].message
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12} lg={7} style={{ width: "inherit" }}>
            <MentionInput
              mentions={isDynamic ? mentionsData : []}
              preValue={definitionClause}
              inputChange={handleChangeMentions}
              label="Definición de cláusula *"
              isError={
                !!errors.hasOwnProperty("definitionClause") &&
                !!errors["definitionClause"].message
              }
              errorMessage={
                errors.hasOwnProperty("definitionClause") &&
                errors["definitionClause"].message
              }
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              fullWidth
              size="small"
              shrink={true}
              notched={true}
              control={
                <Switch
                  title="¿Es dinamica?"
                  checked={isDynamic}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label="¿Es dinámica?"
            />
          </Grid>

          <Grid container style={{ textAlign: "end", paddingRight: "9px" }}>
            <Grid item xs={12} md={12} lg={12}>
              <Button
                variant="outlined"
                style={{ backgroundColor: "transparent", color: "#7EBDCE" }}
                onClick={closeModal}
              >
                REGRESAR
              </Button>
              &nbsp;&nbsp;
              <Button
                variant="outlined"
                disabled={submitDisabled}
                style={{
                  backgroundColor: "#7EBDCE",
                  border: "none",
                  color: "#ffffff",
                }}
                type="submit"
              >
                GUARDAR
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default EditClausesContracts;
